import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { useOkapiKy } from '@folio/stripes/core';
import { useQueryClient } from '@tanstack/react-query';
import ReportsList from '../components/reports/management/list/ReportsList';
import useReportsAPI from '../hooks/useReportsAPI';
import useCategoriesAPI from '../hooks/useCategoriesAPI';
import useReportTypesAPI from '../hooks/useReportTypesAPI';

const PER_PAGE = 30;

function ReportsListRoute({ stripes }) {
  const location    = useLocation();
  const history     = useHistory();
  const okapiKy     = useOkapiKy();
  const queryClient = useQueryClient();

  // Parse URL search params — this is the single source of truth
  const searchParams = new URLSearchParams(location.search);

  const [offset, setOffset] = useState(0);

  // Fetch data
  const { reports, totalRecords, isLoading, isFetching } = useReportsAPI({ searchParams, offset });
  const { categories }  = useCategoriesAPI();
  const { reportTypes } = useReportTypesAPI();

  // querySetter — updates URL only, no stripesConnect state
  const querySetter = useCallback(({ nsValues }) => {
    const params = new URLSearchParams(location.search);

    Object.entries(nsValues).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    // Reset offset on filter/search change
    setOffset(0);

    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    if (`${location.pathname}${location.search}` !== newUrl) {
      history.push(newUrl);
    }
  }, [location, history]);

  // queryGetter — reads from URL
  const queryGetter = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const result = {};
    params.forEach((value, key) => { result[key] = value; });
    return result;
  }, [location.search]);

  const onNeedMoreData = useCallback((askAmount, index) => {
    if (index >= 0) {
      setOffset(index);
    } else {
      setOffset(prev => prev + PER_PAGE);
    }
  }, []);

  // Build a source-like object for ReportsList compatibility
  const source = {
    totalCount: () => totalRecords,
    loaded:     () => !isLoading,
    pending:    () => isFetching,
    failure:    () => false,
    failureMessage: () => '',
    fetchMore:  () => setOffset(prev => prev + PER_PAGE),
    fetchOffset: (index) => setOffset(index),
    update:     () => {},
  };

  // Build resources-like object for Filters component
  const resources = {
    reports:     { records: reports, other: { totalRecords }, hasLoaded: !isLoading },
    categories:  { records: categories },
    reportTypes: { records: reportTypes },
  };

  // Build mutator-like object
  const mutator = {
    resultOffset: { replace: (val) => setOffset(val) },
    reports: {
      DELETE: ({ id }) => okapiKy.delete(`reports/${id}`).then(() => {
        queryClient.invalidateQueries(['reports']);
      }),
    },
  };

  return (
    <ReportsList
      source={source}
      initialSearch="?sort=name"
      onNeedMoreData={onNeedMoreData}
      queryGetter={queryGetter}
      querySetter={querySetter}
      resources={resources}
      mutator={mutator}
      history={history}
      location={location}
      stripes={stripes}
    />
  );
}

ReportsListRoute.propTypes = {
  stripes: PropTypes.object,
};

export default ReportsListRoute;
